import React, { useEffect, useState } from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import { Button, Card, Icon, Switch, Table, Tooltip, message } from 'antd';
import { getMarketplaceSubscriptions, updateMarketplaceSubscriptionConfig } from '../api/marketplace';
import { useOwnerReportingHost } from '../hooks/useOwnerReportingHost';
import { buildMarketplaceOwnerReportingUri } from 'utils/routes';
import { buildTabHref } from './pageFilters';

const SettingsPage = ({ location }) => {
  const { hostId, isAdmin } = useOwnerReportingHost();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!hostId) return;
    setLoading(true);
    try {
      const subs = (await getMarketplaceSubscriptions(hostId)) || [];
      setSubscription(subs.find(s => s.productCode === 'owner-reporting' && s.status === 'active') || null);
    } catch (e) {
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostId, isAdmin]);

  const setLiveUpdate = async checked => {
    if (!subscription) return;
    try {
      const updated = await updateMarketplaceSubscriptionConfig({
        hostId,
        productCode: 'owner-reporting',
        productVersion: subscription.productVersion || 'v1',
        config: { ...subscription.config, liveUpdate: !!checked }
      });
      setSubscription(updated);
      message.success('Updated');
    } catch (e) {
      message.error('Update failed');
    }
  };

  const toggle = async (event, actionCode, enabled) => {
    if (!subscription) return;
    const eventActions = [...((subscription.config && subscription.config.eventActions) || [])];
    const idx = eventActions.findIndex(ea => ea.event === event && ea.actionCode === actionCode);
    if (idx >= 0) {
      eventActions[idx] = { ...eventActions[idx], enabled };
    } else {
      eventActions.push({ event, actionCode, enabled });
    }
    try {
      const updated = await updateMarketplaceSubscriptionConfig({
        hostId,
        productCode: 'owner-reporting',
        productVersion: subscription.productVersion || 'v1',
        config: { ...subscription.config, eventActions }
      });
      setSubscription(updated);
      message.success('Updated');
    } catch (e) {
      message.error('Update failed');
    }
  };

  const rows = [
    { event: 'newReservation', actionCode: 'ownerReporting.syncReservationRevenue', label: 'Sync revenue on new booking' },
    { event: 'updateReservation', actionCode: 'ownerReporting.syncReservationRevenue', label: 'Sync revenue on update' },
    { event: 'cancelReservation', actionCode: 'ownerReporting.syncReservationRevenue', label: 'Sync revenue on cancel' },
    { event: 'updateReservation', actionCode: 'ownerReporting.recalcUnitMonth', label: 'Recalc month on update' },
    { event: 'cancelReservation', actionCode: 'ownerReporting.recalcUnitMonth', label: 'Recalc month on cancel' }
  ].map(r => {
    const eventActions = (subscription && subscription.config && subscription.config.eventActions) || [];
    const toggleState = eventActions.find(ea => ea.event === r.event && ea.actionCode === r.actionCode);
    return { ...r, enabled: toggleState ? toggleState.enabled !== false : true };
  });

  if (!isAdmin) {
    const dashboard = buildMarketplaceOwnerReportingUri('dashboard');
    const to = buildTabHref(dashboard, location && location.search, hostId);
    return <Redirect to={to} />;
  }

  return (
    <Card title="Owner Reporting — Event Actions" loading={loading}>
      {!subscription && <p>Enable Owner Reporting from the Marketplace catalog first.</p>}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: 12 }}>
            Live Update{' '}
            <Tooltip
              overlayStyle={{ maxWidth: 420 }}
              title="Yes recalculates an unaudited report when the owner opens that month. No shows the last saved snapshot. Audited reports stay unchanged."
            >
              <Icon type="question-circle" style={{ color: 'rgba(0,0,0,0.45)' }} />
            </Tooltip>
          </span>
          <Switch
            disabled={!subscription}
            checked={!!(subscription && subscription.config && subscription.config.liveUpdate)}
            checkedChildren="Yes"
            unCheckedChildren="No"
            onChange={setLiveUpdate}
          />
        </div>
        <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.45)' }}>
          Yes recalculates an unaudited report when the owner opens that month. No shows the last saved snapshot. Audited reports stay unchanged.
        </div>
      </div>
      <Table
        rowKey={r => `${r.event}:${r.actionCode}`}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: 'Action', dataIndex: 'label' },
          { title: 'Event', dataIndex: 'event' },
          {
            title: 'Enabled',
            render: r => <Switch disabled={!subscription} checked={r.enabled} onChange={checked => toggle(r.event, r.actionCode, checked)} />
          }
        ]}
      />
      <Button style={{ marginTop: 16 }} onClick={load}>
        Refresh
      </Button>
    </Card>
  );
};

export default withRouter(SettingsPage);
